import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import useVideoVisibility from '../hooks/useVideoVisibility';
import { scrollToSection } from '../utils/scrollUtils';

const HeroSection = ({ stats }) => {
  const navigate = useNavigate();
  const {
    videoRef,
    isLoaded,
    hasError
  } = useVideoVisibility({
    threshold: 0.3,
    rootMargin: '100px',
    pauseDelay: 150,
    resumeDelay: 100,
    respectMotionPrefs: true,
    enableSmartPreload: true,
    enablePerformanceMonitoring: false,
    debug: false
  });

  return (
    <section className="hero-bottom-fade section-glow-bottom relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="hero-video-container">
        <video
          ref={videoRef}
          className="hero-video hero-video-optimized"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Crect width='1920' height='1080' fill='%23111827'/%3E%3C/svg%3E"
          aria-label="Background video showcasing digital solutions"
        >
          {/* Primary source: WebM VP9 */}
          <source 
            src="/assets/loop2.webm" 
            type="video/webm; codecs=vp9" 
          />
          
          {/* Fallback: MP4 H.264 */}
          <source 
            src="/assets/loop2.mp4" 
            type="video/mp4" 
          />
          
          {/* Fallback message for browsers that don't support video */}
          Your browser does not support the video tag.
        </video>
        
        {/* Simple loading state */}
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 bg-slate-900 z-5"></div>
        )}

        {/* Error fallback */}
        {hasError && (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-5"></div>
        )}

        <div className="hero-overlay" />
      </div>
      
      <div className="relative z-10 text-center max-w-6xl mx-auto px-4 pt-24 md:pt-20 pb-20 md:pb-8">
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-4 md:mb-6 leading-tight">
          <span className="bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
            Elevate Your
          </span>
          <br />
          <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
            Digital Presence
          </span>
        </h1>

        <p className="text-base sm:text-lg md:text-2xl text-slate-300 mb-6 md:mb-8 max-w-4xl mx-auto leading-relaxed px-2">
          Comprehensive web development, software protection, and IT outsourcing solutions
          delivering cutting-edge technology tailored to elevate your business.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-10 md:mb-16 px-4 sm:px-0">
          <button
            className="premium-btn flex items-center justify-center text-sm md:text-base"
            onClick={() => navigate('/contact')}
          >
            Get Started <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
          </button>
          <button
            className="secondary-btn text-sm md:text-base"
            onClick={() => scrollToSection('services')}
          >
            View Our Work
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-4xl mx-auto px-2">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent mb-1 md:mb-2">
                {stat.number}
              </div>
              <div className="text-slate-400 text-xs md:text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator - hidden on mobile */}
      <div className="hidden md:block absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
        <ChevronDown className="h-6 w-6 text-slate-400" />
      </div>
    </section>
  );
};

export default HeroSection;